import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_OPTIONS = [
  { value: "open", label: "Open · Plenty of room" },
  { value: "limited", label: "Limited · A few spots left" },
  { value: "closed", label: "Closed · Waitlist only" },
];

const STATUS_DOT = {
  open: "bg-emerald-400",
  limited: "bg-gold",
  closed: "bg-rose-400",
};

function dotClassFor(status) {
  return STATUS_DOT[status] || STATUS_DOT.limited;
}

function pulseClassFor(status) {
  if (status === "closed") return null;
  return status === "open" ? "bg-emerald-400" : "bg-gold";
}

function PreviewDesktop({ form }) {
  return (
    <div className="bg-white border-b border-neutral-200/70 h-10 flex items-center justify-center gap-3 px-4 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-neutral-500">
      {form.enabled ? (
        <>
          <span className="relative flex h-2 w-2 flex-none">
            <span className={`relative inline-flex rounded-full h-2 w-2 ${dotClassFor(form.status)}`} />
            {pulseClassFor(form.status) && (
              <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${pulseClassFor(form.status)}`} />
            )}
          </span>
          <span className="truncate max-w-[260px]">
            {form.text || "Banner text…"}
          </span>
          <span className="text-gold flex-none">
            {form.status === "closed" ? "Waitlist →" : "Apply →"}
          </span>
        </>
      ) : (
        <span className="text-neutral-400 italic">Banner is hidden</span>
      )}
    </div>
  );
}

function PreviewMobile({ form }) {
  return (
    <div className="bg-white border border-neutral-200/70 h-9 flex items-center justify-center gap-2 px-3 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
      {form.enabled ? (
        <>
          <span className={`block h-2 w-2 rounded-full flex-none ${dotClassFor(form.status)}`} />
          <span className="truncate">
            {form.short_text || form.text || "Banner text…"}
          </span>
          <span className="text-gold flex-none">
            {form.status === "closed" ? "Waitlist →" : "Apply →"}
          </span>
        </>
      ) : (
        <span className="text-neutral-400 italic">Banner is hidden</span>
      )}
    </div>
  );
}

export default function AvailabilityPanel() {
  const [form, setForm] = useState({
    enabled: true,
    status: "limited",
    text: "",
    short_text: "",
    slots_remaining: 2,
    quarter: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/availability");
      setForm(r.data);
    } catch {
      toast.error("Failed to load availability.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        slots_remaining: Number(form.slots_remaining) || 0,
      };
      await api.put("/settings/availability", payload);
      toast.success("Banner updated. Live in seconds.");
    } catch {
      toast.error("Failed to save availability.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-neutral-500 text-sm">Loading availability…</div>;
  }

  return (
    <form
      onSubmit={save}
      data-testid="availability-form"
      className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10"
    >
      <div className="lg:col-span-7 border border-neutral-200 bg-[#FAFAFA] p-6 sm:p-8 lg:p-10 space-y-7">
        <div className="flex items-center gap-3">
          <Sparkles size={16} className="text-gold" strokeWidth={1.5} />
          <p className="overline">Site banner</p>
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
          Capacity, on{" "}
          <span className="italic text-aurora">your terms.</span>
        </h2>
        <p className="text-neutral-500 text-sm leading-relaxed max-w-md">
          Drives urgency on every page. Toggle it off, change the message,
          mark yourself fully booked. Live in seconds, no redeploy needed.
        </p>

        <label className="flex items-start gap-4 cursor-pointer pt-2 border-t border-neutral-200">
          <input
            type="checkbox"
            checked={!!form.enabled}
            onChange={update("enabled")}
            data-testid="availability-enabled"
            className="mt-5 accent-gold w-4 h-4"
          />
          <div className="pt-4">
            <div className="text-neutral-900 text-sm">Show banner on site</div>
            <div className="text-neutral-500 text-xs mt-1">
              Uncheck to hide the banner sitewide.
            </div>
          </div>
        </label>

        <div>
          <label className="overline block mb-3" htmlFor="av-status">Status</label>
          <select
            id="av-status"
            value={form.status}
            onChange={update("status")}
            data-testid="availability-status"
            className="editorial-input bg-white"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-white">
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-3" htmlFor="av-slots">Slots remaining</label>
            <input
              id="av-slots"
              type="number"
              min={0}
              max={99}
              value={form.slots_remaining}
              onChange={update("slots_remaining")}
              data-testid="availability-slots"
              className="editorial-input"
            />
          </div>
          <div>
            <label className="overline block mb-3" htmlFor="av-quarter">Quarter</label>
            <input
              id="av-quarter"
              value={form.quarter}
              onChange={update("quarter")}
              data-testid="availability-quarter"
              className="editorial-input"
              placeholder="Q2 2026"
            />
          </div>
        </div>

        <div>
          <label className="overline block mb-3" htmlFor="av-text">Banner text (desktop)</label>
          <input
            id="av-text"
            value={form.text}
            onChange={update("text")}
            data-testid="availability-text-input"
            className="editorial-input"
            placeholder="Currently taking 2 new clients for Q2 2026"
            maxLength={200}
          />
        </div>

        <div>
          <label className="overline block mb-3" htmlFor="av-short">Banner text (mobile, shorter)</label>
          <input
            id="av-short"
            value={form.short_text}
            onChange={update("short_text")}
            data-testid="availability-short-input"
            className="editorial-input"
            placeholder="2 spots open · Q2 2026"
            maxLength={80}
          />
        </div>

        <div className="flex justify-end pt-4 border-t border-neutral-200">
          <button
            type="submit"
            disabled={saving}
            data-testid="availability-save"
            className="btn-gold disabled:opacity-60"
          >
            <Save size={14} />
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="lg:col-span-5 space-y-4">
        <p className="overline">Live preview</p>
        <div className="border border-neutral-200 overflow-hidden">
          <PreviewDesktop form={form} />
          <div className="p-5 sm:p-6 bg-[#FAFAFA] text-xs text-neutral-500 leading-relaxed">
            <p className="text-neutral-700 mb-2 text-sm">Mobile</p>
            <PreviewMobile form={form} />
          </div>
        </div>
        <p className="text-xs text-neutral-400 leading-relaxed">
          Tip: when you mark yourself <em className="text-gold not-italic">closed</em>,
          the CTA flips to &ldquo;Waitlist&rdquo; so prospects still convert into your
          pipeline for next quarter.
        </p>
      </div>
    </form>
  );
}
